import { useMemo, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { ChevronDown, ChevronRight, Loader2, TriangleAlert } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { patchShelf } from '@/api/shelves';
import {
  ANSWER_COLOR, ANSWER_LABEL, ANSWER_ORDER, MODEL_LABEL, getShelfChart,
  type ShelfChartPhoto, type ShelfChartResponse, type ShelfModel,
} from '@/api/calibrationChart';
import DistributionChart, { type DistBin, type DistMarker } from './DistributionChart';
import { ChartPeriod, daysAgo, today, windowParams } from './ChartPeriod';

// AI197 (#1165, #1177, #1181–#1189): график порога полки. Ось — абсолютная (сырое число
// модели), eff — подписью под бинами; столбцы по статусу итогового алерта; фото сверху,
// колонками над бинами; порог и делитель — маркеры, перетаскиваются и сохраняются при
// отпускании; период — наш пикер с пресетом «с обновления модели». Модели не смешиваются:
// при смеси в окне — предупреждение с датой и второй график по другой модели.

const SHELF_LEGEND = Object.fromEntries(ANSWER_ORDER.map((k) => [k, { label: ANSWER_LABEL[k], color: ANSWER_COLOR[k] }]));
const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`);

function fmtDt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function photoCaption(p: ShelfChartPhoto): string {
  const single = p.answer !== 'none' && p.answer !== 'FULL'
    ? (p.single ? ' · ниже нормы была только эта полка' : ' · ниже нормы были и другие полки')
    : '';
  return `abs ${pct(p.raw)} (eff ${pct(p.eff)}) · ${fmtDt(p.at)} · ${ANSWER_LABEL[p.answer]}${single}`;
}

type Thresholds = ShelfChartResponse['thresholds'];

function ChartBody({ data, onSaved, onPreview, editable }: {
  data: ShelfChartResponse;
  onSaved?: () => void;
  /** Сразу показать новые значения, не дожидаясь ответа: иначе маркер после отпускания на
   *  секунду прыгает назад. */
  onPreview?: (next: Partial<Thresholds>) => void;
  editable: boolean;
}) {
  const t = data.thresholds;
  const bins: DistBin[] = useMemo(() => data.bins.map((b) => ({
    lo: b.lo, hi: b.hi, counts: b.counts,
    label: `${pct(b.lo)}–${pct(b.hi)}`,
    sub: `eff ${pct(b.eff_lo)}–${pct(b.eff_hi)}`,
    photos: b.photos.map((p) => ({ key: String(p.frame_id), url: p.url, caption: photoCaption(p), answer: p.answer, polygons: p.boxes, outlines: p.outline ? [p.outline] : undefined })),
  })), [data]);
  const [saving, setSaving] = useState<string | null>(null);
  const save = async (payload: Parameters<typeof patchShelf>[1], what: string) => {
    setSaving(what);
    try {
      await patchShelf(String(data.shelf.id), payload);
    } finally {
      setSaving(null);
      // И при ошибке: перечитать, чтобы откатить показанное заранее (onPreview).
      onSaved?.();
    }
  };
  const markers: DistMarker[] = [
    {
      key: 'thr', x: t.threshold_abs, color: '#dc2626',
      label: `порог: abs ${pct(t.threshold_abs)} · eff ${pct(t.empty_shelf_score)}`,
      min: 0, max: 1,
      // Порог хранится в eff → abs / делитель.
      onChange: editable ? (x) => {
        const score = +(x / t.divisor).toFixed(3);
        onPreview?.({ empty_shelf_score: score, threshold_abs: x });
        void save({ empty_shelf_score: score }, 'порог');
      } : undefined,
    },
    {
      key: 'div', x: t.divisor, color: '#6b7280', dashed: true,
      label: `делитель ${pct(t.divisor)} (eff 100%)`,
      min: 0.05, max: 1,
      // Бэкенд при смене делителя пересчитывает порог (eff), чтобы красная линия — граница по
      // сырому — осталась на месте: двигается шкала, не алерты. Порог — своим маркером.
      onChange: editable ? (x) => {
        const div = +x.toFixed(3);
        // Абсолютный порог на месте, eff пересчитывается — так же, как сделает бэкенд.
        onPreview?.({ model_correction_max_pct: div, divisor: div, empty_shelf_score: Math.min(1, t.threshold_abs / div) });
        void save({ model_correction_max_pct: div }, 'делитель');
      } : undefined,
    },
  ];
  if (data.stats.n === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        Нет замеров {MODEL_LABEL[data.model]} моделью за выбранный период
        {data.mode === 'alerts' ? ' с ответом на алерт' : ''}.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <DistributionChart
        bins={bins} legend={SHELF_LEGEND} order={ANSWER_ORDER} markers={markers}
        xTickFormatter={(v) => pct(v)}
        emptyText="Нет замеров за выбранный период."
      />
      <div className="text-[11px] text-muted-foreground">
        {saving && <span className="mr-2 text-foreground"><Loader2 className="mr-1 inline size-3 animate-spin" aria-hidden />сохраняю {saving}…</span>}
        Замеров: {data.stats.n} · ниже порога: {data.stats.below_threshold}
        {data.stats.share_below != null && ` (${Math.round(data.stats.share_below * 100)}%)`}
        {' · '}с ответом на алерт: {data.stats.answered}
        {data.model_deployed_at && ` · модель выложена на точку ${fmtDt(data.model_deployed_at)}`}
      </div>
    </div>
  );
}

/** Второй график — периоды другой модели (свёрнут по умолчанию). */
function OtherModelChart({ shelfId, win, mode, other }: {
  shelfId: number; win: { from: string; to: string }; mode: 'alerts' | 'all'; other: ShelfModel;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useSWR(
    open ? ['calibration:shelf-chart', shelfId, 'other', mode, win.from, win.to] : null,
    () => getShelfChart({ shelf_id: shelfId, mode, model: 'other', ...win }).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  return (
    <div className="rounded-md border border-border p-2">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs font-medium text-foreground">
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        Другая модель ({MODEL_LABEL[other]}) — распределение за тот же период
      </button>
      {open && (isLoading || !data
        ? <div className="flex h-16 items-center justify-center"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>
        : (
          <div className="mt-2 space-y-2">
            <div className="text-[11px] text-muted-foreground">
              Порог и делитель здесь — текущие, под {MODEL_LABEL[data.shelf.model]} модель; для {MODEL_LABEL[other]} они почти наверняка другие.
            </div>
            <ChartBody data={data} editable={false} />
          </div>
        ))}
    </div>
  );
}

export default function ShelfThresholdChart({ shelfId, refreshKey, onChanged, onThresholdsPreview }: {
  shelfId: number; refreshKey?: number; onChanged?: () => void;
  /** Отпустили маркер — новые значения сразу наверх, в поля плашки (Алексей 21.10 #7022). */
  onThresholdsPreview?: (next: Record<string, number | boolean>) => void;
}) {
  const [mode, setMode] = useState<'alerts' | 'all'>('alerts');
  const [from, setFrom] = useState(daysAgo(14));
  const [to, setTo] = useState(today());
  const win = windowParams(from, to);
  const { data, isLoading, error, mutate } = useSWR(
    ['calibration:shelf-chart', shelfId, 'current', mode, win.from, win.to, refreshKey],
    () => getShelfChart({ shelf_id: shelfId, mode, ...win }).then((r) => r.data),
    // Новый ключ (refreshKey после правки в шапке, период) — держим прошлый график, пока
    // грузится новый: без этого он пропадал под спиннер и страница «перезагружалась».
    { revalidateOnFocus: false, keepPreviousData: true },
  );
  const onSaved = () => { void mutate(); onChanged?.(); };
  const onPreview = (next: Partial<Thresholds>) => {
    void mutate((prev) => (prev ? { ...prev, thresholds: { ...prev.thresholds, ...next } } : prev), { revalidate: false });
    onThresholdsPreview?.(next as Record<string, number | boolean>);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <ToggleGroup value={mode} onChange={(v) => v && setMode(v as 'alerts' | 'all')}>
          <ToggleGroupItem value="alerts">Кадры алертов</ToggleGroupItem>
          <ToggleGroupItem value="all">Все замеры</ToggleGroupItem>
        </ToggleGroup>
        <ChartPeriod from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} modelDeployedAt={data?.model_deployed_at} />
        {data && (
          <span className="ml-auto text-muted-foreground">
            на графике: <b className="text-foreground">{MODEL_LABEL[data.model]}</b> модель
          </span>
        )}
      </div>
      {isLoading && !data && <div className="flex h-24 items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden /></div>}
      {error && !data && <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />}
      {data && (
        <>
          {data.mixed && (
            <div className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300">
              <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
              <span>
                В этом периоде полку считали две модели: смена {fmtDt(data.switch_at)}. Модели не смешиваем — на графике
                только {MODEL_LABEL[data.model]} ({data.periods.find((p) => p.model === data.model)?.n ?? 0} замеров),
                другая — во втором графике ниже.
              </span>
            </div>
          )}
          <ChartBody data={data} onSaved={onSaved} onPreview={onPreview} editable />
          {data.mixed && (
            <OtherModelChart shelfId={shelfId} win={win} mode={mode} other={data.model === 'pct' ? 'yolo' : 'pct'} />
          )}
        </>
      )}
    </div>
  );
}
